import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy
} from 'firebase/firestore';
import { db, auth } from './init';

/*
 * AR Examples — a curated set of admin-published example sketches.
 *
 * An example is an ordinary `projects/{id}` doc (visibility Public); membership is tracked by a doc in
 * the top-level `examples` collection whose id IS the project id (natural dedup + a cheap exists()
 * check in the security rules). Only the project id is authoritative here — names/thumbnails are read
 * from the referenced project, so an example always reflects the live project. Writes are admin-only
 * (enforced by firestore.rules); listing is world-readable (including logged-out visitors).
 */

export interface ExampleRow {
  /** The referenced project's id (== the examples doc id). */
  id: string;
  name: string;
  ownerUsername: string;
  visibility: 'Public' | 'Private';
  createdAt: number;
  updatedAt: number;
}

function tsToMillis(value: unknown): number {
  // Firestore Timestamp (and our test doubles) expose toMillis(); already-normalized reads are numbers.
  if (value && typeof (value as any).toMillis === 'function')
    return (value as any).toMillis();
  if (typeof value === 'number') return value;
  return 0;
}

/**
 * List the published examples, newest-first. Reads the `examples` collection, then reads each
 * referenced project. A referenced project that has been deleted or flipped to Private is skipped
 * (dangling ids are tolerated — "unlist" is a separate admin action from deleting the project), so
 * the returned rows are always openable Public sketches. Order follows the examples collection.
 */
export async function listExamples(): Promise<ExampleRow[]> {
  const q = query(collection(db, 'examples'), orderBy('addedAt', 'desc'));
  const snap = await getDocs(q);
  const ids = snap.docs.map((d) => d.id);
  const rows = await Promise.all(
    ids.map(
      async (id): Promise<ExampleRow | null> => {
        try {
          const projectSnap = await getDoc(doc(db, 'projects', id));
          if (!projectSnap.exists()) return null;
          const data = projectSnap.data() as any;
          if (data.visibility !== 'Public') return null;
          return {
            id,
            name: data.name,
            ownerUsername: data.ownerUsername,
            visibility: data.visibility,
            createdAt: tsToMillis(data.createdAt),
            updatedAt: tsToMillis(data.updatedAt)
          };
        } catch {
          // A single unreadable project must not fail the whole list.
          return null;
        }
      }
    )
  );
  return rows.filter((r): r is ExampleRow => r !== null);
}

/** Admin: register a project as an example. Idempotent (doc id is the project id). */
export async function addExample(projectId: string): Promise<void> {
  const uid = auth.currentUser?.uid ?? null;
  await setDoc(doc(db, 'examples', projectId), {
    projectId,
    addedBy: uid,
    addedAt: serverTimestamp()
  });
}

/** Admin: unregister a project as an example (the project itself is left untouched). */
export async function removeExample(projectId: string): Promise<void> {
  await deleteDoc(doc(db, 'examples', projectId));
}

/** Whether a project is currently registered as an example. */
export async function isExample(projectId: string): Promise<boolean> {
  const snap = await getDoc(doc(db, 'examples', projectId));
  return snap.exists();
}
